(ns yardwerkz.web.booking.outline
  "Step 3: Click-to-place polygon editor on Mapbox GL JS satellite map.
   Users click to place vertices, double-click or click near first vertex to close."
  (:require
   ["mapbox-gl" :as mapboxgl]
   ["@turf/turf" :as turf]
   [uix.core :refer [defui $ use-state use-effect use-ref use-callback]]
   [io.factorhouse.rfx.core :as rfx]
   [yardwerkz.web.store :as store]
   [yardwerkz.web.config :as config]
   [yardwerkz.web.domain.outline :as outline]
   [yardwerkz.web.ui.components :as ui]
   [yardwerkz.web.ui.icons :as icons]
   [lambdaisland.glogi :as log]))

;; =============================================================================
;; Constants
;; =============================================================================

(def ^:private satellite-style "mapbox://styles/mapbox/satellite-streets-v12")
(def ^:private outline-zoom 19)
(def ^:private close-threshold-px 15)

;; Zone tag colors for fills
(def ^:private tag-colors
  {"front" "#22c55e"
   "back" "#3b82f6"
   "side-left" "#f59e0b"
   "side-right" "#ef4444"
   "garden" "#a855f7"
   "patio" "#06b6d4"
   "driveway" "#6b7280"
   "other" "#ec4899"})

(defn- zone-color [tag]
  (get tag-colors tag "#22c55e"))

;; =============================================================================
;; GeoJSON Helpers
;; =============================================================================

(defn- coords->geojson-polygon
  "Convert [[lng lat]...] to GeoJSON Polygon feature."
  [coords & {:keys [id tag]}]
  #js {:type "Feature"
       :properties #js {:id (or id "") :tag (or tag "")}
       :geometry #js {:type "Polygon"
                      :coordinates (clj->js [coords])}})

(defn- zones->geojson
  "Convert zones vector to GeoJSON FeatureCollection."
  [zones]
  #js {:type "FeatureCollection"
       :features (clj->js
                  (mapv (fn [{:keys [id coords tag]}]
                          {:type "Feature"
                           :properties {:id id :tag (or tag "")}
                           :geometry {:type "Polygon"
                                      :coordinates [coords]}})
                        zones))})

(defn- vertices->geojson
  "Convert vertex points to GeoJSON for circle markers."
  [vertices]
  #js {:type "FeatureCollection"
       :features (clj->js
                  (map-indexed
                   (fn [idx [lng lat]]
                     {:type "Feature"
                      :properties {:index idx}
                      :geometry {:type "Point"
                                 :coordinates [lng lat]}})
                   vertices))})

(defn- rubber-band-geojson
  "GeoJSON line from last vertex to cursor position."
  [vertices hover-point]
  (when (and (seq vertices) hover-point)
    #js {:type "Feature"
         :geometry #js {:type "LineString"
                        :coordinates (clj->js [(last vertices) hover-point])}}))

(defn- draft-polygon-geojson
  "GeoJSON polygon from vertices (for fill preview)."
  [vertices]
  (when (>= (count vertices) 3)
    (let [closed (conj (vec vertices) (first vertices))]
      #js {:type "Feature"
           :geometry #js {:type "Polygon"
                          :coordinates (clj->js [closed])}})))

;; =============================================================================
;; Area Calculation
;; =============================================================================

(defn- calculate-area-sqft
  "Calculate area of a polygon in square feet using Turf."
  [coords]
  (try
    (let [closed (outline/close-ring-once coords)
          polygon (turf/polygon (clj->js [closed]))
          area-m2 (turf/area polygon)]
      (* area-m2 10.7639)) ;; m² to sq ft
    (catch :default _e 0)))

(defn- format-area [sqft]
  (if (> sqft 43560)
    (str (.toFixed (/ sqft 43560) 2) " acres")
    (str (js/Math.round sqft) " sq ft")))

;; =============================================================================
;; Map Setup
;; =============================================================================

(defn- setup-map-sources-and-layers!
  "Add GeoJSON sources and layers to the map for zone rendering."
  [^js m]
  ;; Completed zones fill
  (.addSource m "completed-zones"
              #js {:type "geojson"
                   :data #js {:type "FeatureCollection" :features #js []}})
  (.addLayer m #js {:id "completed-zones-fill"
                    :type "fill"
                    :source "completed-zones"
                    :paint #js {:fill-color #js ["get" "color"]
                                :fill-opacity 0.35}})
  (.addLayer m #js {:id "completed-zones-line"
                    :type "line"
                    :source "completed-zones"
                    :paint #js {:line-color #js ["get" "color"]
                                :line-width 3}})

  ;; Draft polygon fill + outline
  (.addSource m "draft-fill"
              #js {:type "geojson"
                   :data #js {:type "Feature" :geometry #js {:type "Point" :coordinates #js [0 0]}}})
  (.addLayer m #js {:id "draft-fill-layer"
                    :type "fill"
                    :source "draft-fill"
                    :paint #js {:fill-color "#22c55e"
                                :fill-opacity 0.35}})
  (.addLayer m #js {:id "draft-line-layer"
                    :type "line"
                    :source "draft-fill"
                    :paint #js {:line-color "#22c55e"
                                :line-width 3}})

  ;; Draft edge line (connects placed vertices, visible with 2+ points)
  (.addSource m "draft-edge"
              #js {:type "geojson"
                   :data #js {:type "Feature" :geometry #js {:type "Point" :coordinates #js [0 0]}}})
  (.addLayer m #js {:id "draft-edge-layer"
                    :type "line"
                    :source "draft-edge"
                    :paint #js {:line-color "#22c55e"
                                :line-width 3}})

  ;; Draft vertices (circles)
  (.addSource m "draft-vertices"
              #js {:type "geojson"
                   :data #js {:type "FeatureCollection" :features #js []}})
  (.addLayer m #js {:id "draft-vertices-layer"
                    :type "circle"
                    :source "draft-vertices"
                    :paint #js {:circle-radius 6
                                :circle-color "#22c55e"
                                :circle-stroke-color "#ffffff"
                                :circle-stroke-width 2}})

  ;; Rubber band line
  (.addSource m "rubber-band"
              #js {:type "geojson"
                   :data #js {:type "Feature" :geometry #js {:type "Point" :coordinates #js [0 0]}}})
  (.addLayer m #js {:id "rubber-band-layer"
                    :type "line"
                    :source "rubber-band"
                    :paint #js {:line-color "#22c55e"
                                :line-width 2
                                :line-dasharray #js [4 4]}}))

(defn- update-completed-zones!
  "Update the completed zones GeoJSON on the map."
  [^js m zones]
  (when-let [src (.getSource m "completed-zones")]
    (let [fc #js {:type "FeatureCollection"
                  :features (clj->js
                             (mapv (fn [{:keys [id coords tag]}]
                                     {:type "Feature"
                                      :properties {:id id :tag (or tag "") :color (zone-color tag)}
                                      :geometry {:type "Polygon"
                                                 :coordinates [coords]}})
                                   zones))}]
      (.setData src fc))))

(defn- update-draft-visuals!
  "Update draft polygon, vertices, edge line, and rubber band on the map."
  [^js m vertices hover-point]
  ;; Fill (3+ vertices)
  (when-let [fill-src (.getSource m "draft-fill")]
    (if (>= (count vertices) 3)
      (.setData fill-src (draft-polygon-geojson vertices))
      (.setData fill-src #js {:type "Feature" :geometry #js {:type "Point" :coordinates #js [0 0]}})))
  ;; Edge line connecting placed vertices (2+ vertices)
  (when-let [edge-src (.getSource m "draft-edge")]
    (if (>= (count vertices) 2)
      (.setData edge-src #js {:type "Feature"
                              :geometry #js {:type "LineString"
                                             :coordinates (clj->js vertices)}})
      (.setData edge-src #js {:type "Feature" :geometry #js {:type "Point" :coordinates #js [0 0]}})))
  ;; Vertex circles
  (when-let [vert-src (.getSource m "draft-vertices")]
    (.setData vert-src (vertices->geojson vertices)))
  ;; Rubber band from last vertex to cursor
  (when-let [rb-src (.getSource m "rubber-band")]
    (if-let [rb (rubber-band-geojson vertices hover-point)]
      (.setData rb-src rb)
      (.setData rb-src #js {:type "Feature" :geometry #js {:type "Point" :coordinates #js [0 0]}}))))

;; =============================================================================
;; Outline Component
;; =============================================================================

(defui outline-step [{:keys [on-next on-back]}]
  (let [dispatch (rfx/use-dispatch)
        address (rfx/use-sub [::store/draft-address])
        saved-geo (rfx/use-sub [::store/draft-geo])

        container-ref (use-ref nil)
        map-ref (use-ref nil)

        [vertices set-vertices] (use-state [])
        [zones set-zones] (use-state (or saved-geo []))
        [hover-point set-hover] (use-state nil)
        [drawing? set-drawing] (use-state true)
        [pending-tag-id set-pending-tag] (use-state nil)

        ;; Refs to hold current state for map event handlers (avoids stale closures)
        vertices-ref (use-ref vertices)
        zones-ref (use-ref zones)
        hover-ref (use-ref hover-point)
        drawing-ref (use-ref drawing?)
        _ (reset! vertices-ref vertices)
        _ (reset! zones-ref zones)
        _ (reset! hover-ref hover-point)
        _ (reset! drawing-ref drawing?)

        ;; Total area across all zones
        total-area (reduce + 0 (map #(calculate-area-sqft (:coords %)) zones))

        close-polygon!
        (use-callback
         (fn [verts]
           (let [closed (outline/close-ring-once verts)]
             (when (outline/valid-ring? closed)
               (let [polygon (turf/polygon (clj->js [closed]))
                     simplified (turf/simplify polygon #js {:tolerance 0.00001 :highQuality true})
                     simplified-coords (js->clj (.. simplified -geometry -coordinates) :keywordize-keys false)
                     final-coords (first simplified-coords)
                     zone-id (str (random-uuid))
                     new-zone {:id zone-id :coords final-coords :tag nil}
                     cur-zones @zones-ref
                     new-zones (conj cur-zones new-zone)]
                 (set-zones new-zones)
                 (set-vertices [])
                 (set-pending-tag zone-id)
                 (dispatch [::store/set-geo new-zones])
                 (when-let [m @map-ref]
                   (update-completed-zones! m new-zones)
                   (update-draft-visuals! m [] nil))))))
         [dispatch])

        handle-click
        (use-callback
         (fn [^js e]
           (when @drawing-ref
             (let [lng (.-lng (.-lngLat e))
                   lat (.-lat (.-lngLat e))
                   point [lng lat]
                   cur-verts @vertices-ref
                   new-verts (conj cur-verts point)]
               (if (and (>= (count cur-verts) 3)
                        (let [^js m @map-ref
                              first-px (.project m (clj->js (first cur-verts)))
                              click-px (.project m (clj->js point))
                              dx (- (.-x click-px) (.-x first-px))
                              dy (- (.-y click-px) (.-y first-px))]
                          (< (js/Math.sqrt (+ (* dx dx) (* dy dy))) close-threshold-px)))
                 (close-polygon! cur-verts)
                 (do
                   (set-vertices new-verts)
                   (when-let [m @map-ref]
                     (update-draft-visuals! m new-verts @hover-ref)))))))
         [close-polygon!])

        handle-dblclick
        (use-callback
         (fn [^js e]
           (.preventDefault e)
           (let [cur-verts @vertices-ref]
             (when (and @drawing-ref (>= (count cur-verts) 3))
               (close-polygon! cur-verts))))
         [close-polygon!])

        handle-mousemove
        (use-callback
         (fn [^js e]
           (let [cur-verts @vertices-ref]
             (when (and @drawing-ref (seq cur-verts))
               (let [lng (.-lng (.-lngLat e))
                     lat (.-lat (.-lngLat e))
                     pt [lng lat]]
                 (set-hover pt)
                 (when-let [m @map-ref]
                   (update-draft-visuals! m cur-verts pt))))))
         [])

        tag-zone!
        (use-callback
         (fn [zone-id tag]
           (let [cur-zones @zones-ref
                 new-zones (mapv (fn [z]
                                   (if (= (:id z) zone-id)
                                     (assoc z :tag tag) z))
                                 cur-zones)]
             (set-zones new-zones)
             (set-pending-tag nil)
             (dispatch [::store/set-geo new-zones])
             (when-let [m @map-ref]
               (update-completed-zones! m new-zones))))
         [dispatch])

        delete-zone!
        (use-callback
         (fn [zone-id]
           (let [cur-zones @zones-ref
                 new-zones (vec (remove #(= (:id %) zone-id) cur-zones))]
             (set-zones new-zones)
             (set-pending-tag nil)
             (dispatch [::store/set-geo new-zones])
             (when-let [m @map-ref]
               (update-completed-zones! m new-zones))))
         [dispatch])

        undo-vertex!
        (use-callback
         (fn []
           (let [cur-verts @vertices-ref]
             (when (seq cur-verts)
               (let [new-verts (vec (butlast cur-verts))]
                 (set-vertices new-verts)
                 (when-let [m @map-ref]
                   (update-draft-visuals! m new-verts @hover-ref))))))
         [])]

    ;; Initialize Mapbox map
    ;; Intentionally not including handlers in deps — they are stable refs
    ;; and we only want to recreate the map when container/address changes
    (use-effect
     (fn []
       (when (and @container-ref (:lng address))
         (set! (.-accessToken mapboxgl) config/MAPBOX_TOKEN)
         (let [m (mapboxgl/Map.
                  #js {:container @container-ref
                       :style satellite-style
                       :center #js [(:lng address) (:lat address)]
                       :zoom outline-zoom
                       :doubleClickZoom false})]
           (reset! map-ref m)
           (.on m "load"
                (fn []
                  (setup-map-sources-and-layers! m)
                  ;; Restore saved zones
                  (when (seq zones)
                    (update-completed-zones! m zones))))
           (.on m "click" handle-click)
           (.on m "dblclick" handle-dblclick)
           (.on m "mousemove" handle-mousemove)
           (fn []
             (.remove m)
             (reset! map-ref nil))))
       js/undefined)
     ^:lint/disable [@container-ref (:lng address) (:lat address)])

    ($ :div {:class "space-y-4"}
       ;; Header
       ($ :div {:class "flex items-center justify-between"}
          ($ :h2 {:class "text-lg font-semibold text-gray-900"} "Outline the Area")
          ($ :div {:class "flex items-center space-x-2"}
             (when (seq vertices)
               ($ ui/button {:variant :ghost :size :sm :on-click undo-vertex!}
                  ($ :span {:class "flex items-center space-x-1"}
                     ($ icons/Undo {:size 14})
                     ($ :span "Undo"))))
             (when (seq zones)
               ($ ui/button {:variant :ghost :size :sm
                             :on-click (fn []
                                         (set-zones [])
                                         (dispatch [::store/set-geo []])
                                         (when-let [m @map-ref]
                                           (update-completed-zones! m [])))}
                  ($ :span {:class "flex items-center space-x-1"}
                     ($ icons/Eraser {:size 14})
                     ($ :span "Clear All"))))))

       ;; Instructions
       ($ :p {:class "text-sm text-gray-500"}
          (if (seq vertices)
            "Click to add vertices. Click near the first point or double-click to close the polygon."
            "Click on the map to start drawing a zone."))

       ;; Map container
       ($ :div {:class "relative rounded-xl overflow-hidden border border-gray-200"
                :style {:height "500px"}}
          ($ :div {:ref container-ref
                   :class (str "w-full h-full " (when drawing? "cursor-crosshair"))})

          ;; Area badge
          (when (pos? total-area)
            ($ :div {:class "absolute top-3 right-3 bg-white/90 backdrop-blur px-3 py-1.5 rounded-full text-sm font-medium text-gray-900 shadow-sm"}
               ($ :span {:class "flex items-center space-x-1"}
                  ($ icons/Ruler {:size 14})
                  ($ :span (format-area total-area)))))

          ;; Empty state
          (when (and (empty? zones) (empty? vertices))
            ($ :div {:class "absolute inset-0 flex items-center justify-center pointer-events-none"}
               ($ :div {:class "bg-black/50 text-white px-4 py-2 rounded-full text-sm"}
                  "Click to start drawing"))))

       ;; Tag selection panel (shown after closing a polygon)
       (when pending-tag-id
         ($ :div {:class "bg-white border border-gray-200 rounded-xl p-4 shadow-lg"}
            ($ :h3 {:class "text-sm font-medium text-gray-900 mb-3"} "What is this area?")
            ($ :div {:class "grid grid-cols-4 gap-2"}
               (for [{:keys [id label]} outline/zone-tags]
                 ($ :button {:key id
                             :class "flex flex-col items-center p-2 rounded-lg border border-gray-200 hover:border-green-300 hover:bg-green-50 transition-colors"
                             :on-click #(tag-zone! pending-tag-id id)}
                    ($ :span {:class "text-xs text-gray-900"} label))))
            ($ :button {:class "mt-2 text-xs text-red-500 hover:text-red-700"
                        :on-click #(delete-zone! pending-tag-id)}
               "Delete this zone")))

       ;; Zone list
       (when (seq zones)
         ($ :div {:class "space-y-2"}
            (for [{:keys [id tag coords]} zones]
              (let [area (calculate-area-sqft coords)]
                ($ :div {:key id
                         :class "flex items-center justify-between p-3 bg-gray-50 rounded-lg"}
                   ($ :div {:class "flex items-center space-x-2"}
                      ($ :div {:class "w-3 h-3 rounded-full"
                               :style {:background-color (zone-color tag)}})
                      ($ :span {:class "text-sm font-medium text-gray-900"}
                         (or (outline/get-zone-label tag) "Untagged"))
                      ($ :span {:class "text-sm text-gray-500"}
                         (format-area area)))
                   ($ :button {:class "text-gray-400 hover:text-red-500"
                               :on-click #(delete-zone! id)}
                      ($ icons/X {:size 16})))))))

       ;; Footer note
       (when (seq zones)
         ($ :p {:class "text-xs text-gray-400"}
            (str (count zones) " zone" (when (> (count zones) 1) "s") " — "
                 (format-area total-area) " total"))))))
